<template>
  <div>
    <b-modal
      :id="modalId"
      modal-class="timesheet-modal"
      title="Timesheet Entry"
      scrollable
      hide-footer
      @hidden="onHidden"
    >
      <div v-if="event" class="entry-detail">
        <!-- Header: worker + status -->
        <div class="entry-header">
          <div>
            <div class="entry-worker">
              {{ (event.tradie && event.tradie.name) || 'Unknown Worker' }}
            </div>
            <div class="entry-project">
              {{ projectName }}
              <template v-if="zoneName && zoneName !== '-'">
                · {{ zoneName }}</template
              >
            </div>
          </div>
          <span class="status-pill" :class="eventStatusClass(event)">{{
            eventStatusLabel(event)
          }}</span>
        </div>

        <!-- Times / break / hours -->
        <div class="entry-times">
          <b-icon-clock class="entry-times-icon" />
          <span>
            {{ formatDateTime(event.clocked_in_at, event.timezone) }} →
            {{ formatDateTime(event.clocked_out_at, event.timezone) }}
            <span
              v-if="timezoneLabel(event.timezone)"
              class="text-muted small ml-1"
              >{{ timezoneLabel(event.timezone) }}</span
            >
            <template v-if="event.break_minutes">
              · {{ event.break_minutes }}m break</template
            >
            <template v-if="event.clocked_in_at && event.clocked_out_at">
              · {{ calcHours(event) }}h</template
            >
            <template v-if="isOvernight"> · Overnight shift</template>
          </span>
        </div>

        <!-- Rejection reason -->
        <div v-if="event.admin_comment" class="entry-rejection">
          <div class="entry-rejection-label">Rejection reason</div>
          <div class="entry-rejection-text">{{ event.admin_comment }}</div>
        </div>

        <!-- Sent to supervisor -->
        <div v-if="event.supervisor" class="entry-section">
          <div class="entry-section-label">Sent to</div>
          <div class="entry-comment-text">{{ event.supervisor.name }}</div>
        </div>

        <!-- Tradie comment -->
        <div class="entry-section">
          <div class="entry-section-label">Comment</div>
          <div v-if="event.comment" class="entry-comment-text">
            {{ event.comment }}
          </div>
          <div v-else class="entry-empty">No comment provided</div>
        </div>

        <!-- Photos -->
        <div class="entry-section">
          <div class="entry-section-label">Photos</div>
          <div
            v-if="event.photo_urls && event.photo_urls.length"
            class="entry-photos"
          >
            <div
              v-for="(url, i) in event.photo_urls"
              :key="i"
              class="entry-photo-wrap"
              @click="openLightbox(event.photo_urls, i)"
            >
              <img
                :src="url"
                class="entry-photo-thumb"
                :alt="'Photo ' + (i + 1)"
              />
            </div>
          </div>
          <div v-else class="entry-empty">No photos attached</div>
        </div>

        <!-- Plant & equipment -->
        <div class="entry-section">
          <div class="entry-section-label">Plant &amp; Equipment</div>
          <div
            v-if="event.plant_entries && event.plant_entries.length"
            class="entry-plant-list"
          >
            <div
              v-for="(pe, i) in event.plant_entries"
              :key="(pe.plant_equipment_id || 'plant') + '-' + i"
              class="entry-plant-line"
            >
              {{ pe.plant_name }}
              <template v-if="pe.plant_id_rego"
                >({{ pe.plant_id_rego }})</template
              >
              <span v-if="pe.quantity" class="entry-plant-qty"
                >· {{ pe.quantity }} {{ plantUnitLabel(pe.rate_type) }}</span
              >
            </div>
          </div>
          <div v-else class="entry-empty">No plant or equipment recorded</div>
        </div>

        <!-- Actions -->
        <div v-if="showActions" class="entry-actions">
          <template v-if="!rejecting">
            <b-button variant="success" @click="onApprove">Approve</b-button>
            <b-button variant="danger" @click="rejecting = true"
              >Reject</b-button
            >
          </template>
          <template v-else>
            <b-form-group
              class="entry-reject-reason mb-2"
              label="Rejection reason (optional)"
            >
              <b-form-input
                v-model="rejectComment"
                placeholder="e.g. Times don't match site records"
              />
            </b-form-group>
            <div class="entry-reject-buttons">
              <b-button variant="outline-secondary" @click="cancelReject"
                >Cancel</b-button
              >
              <b-button variant="danger" @click="onReject"
                >Confirm Reject</b-button
              >
            </div>
          </template>
        </div>
      </div>
    </b-modal>

    <!-- Photo lightbox (self-contained so this modal works on any host) -->
    <b-modal
      :id="lightboxId"
      size="xl"
      centered
      hide-header
      :hide-footer="lightboxPhotos.length <= 1"
      body-class="p-1 text-center bg-dark"
      footer-class="justify-content-center"
      @hidden="
        lightboxPhotos = []
        lightboxIndex = 0
      "
    >
      <img
        v-if="lightboxPhotos.length"
        :src="lightboxPhotos[lightboxIndex]"
        style="max-width: 100%; max-height: 80vh; object-fit: contain"
      />
      <template #modal-footer>
        <b-button variant="light" @click="lightboxPrev">&#8592; Prev</b-button>
        <span class="mx-3 text-white"
          >{{ lightboxIndex + 1 }} / {{ lightboxPhotos.length }}</span
        >
        <b-button variant="light" @click="lightboxNext">Next &#8594;</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  zoneDisplayName,
  projectNameFromEvent,
  eventStatusLabel,
  eventStatusClass,
  formatBusinessDateTime,
  bizFromIso,
  timezoneLabel,
  eventMinutes,
  isApprovableEvent,
  plantUnitLabel,
} from '@/utils/timesheetHelpers'

export default {
  name: 'TimesheetEntryDetailModal',
  props: {
    modalId: { type: String, default: 'timesheet-entry-detail-modal' },
    // When true, an approvable entry (see isApprovableEvent) shows
    // Approve / Reject in the modal.
    canApprove: { type: Boolean, default: false },
    // Optional project list so projectNameFromEvent can resolve by id.
    projects: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  data() {
    return {
      event: null,
      rejecting: false,
      rejectComment: '',
      lightboxPhotos: [],
      lightboxIndex: 0,
    }
  },
  computed: {
    lightboxId() {
      return this.modalId + '-lightbox'
    },
    projectName() {
      return this.event ? projectNameFromEvent(this.event, this.projects) : ''
    },
    zoneName() {
      return this.event ? zoneDisplayName(this.event.zone) : '-'
    },
    isOvernight() {
      const e = this.event
      if (!e || !e.clocked_in_at || !e.clocked_out_at) return false
      const inDay = bizFromIso(e.clocked_in_at, e.timezone)
      const outDay = bizFromIso(e.clocked_out_at, e.timezone)
      return outDay.format('YYYY-MM-DD') > inDay.format('YYYY-MM-DD')
    },
    showActions() {
      return this.canApprove && isApprovableEvent(this.event)
    },
  },
  methods: {
    plantUnitLabel,
    eventStatusLabel,
    eventStatusClass,
    timezoneLabel,
    open(event) {
      this.event = event
      this.rejecting = false
      this.rejectComment = ''
      this.$bvModal.show(this.modalId)
    },
    close() {
      this.$bvModal.hide(this.modalId)
    },
    onHidden() {
      this.event = null
      this.rejecting = false
      this.rejectComment = ''
    },
    formatDateTime(iso, tz) {
      return iso ? formatBusinessDateTime(iso, undefined, tz) : '–'
    },
    calcHours(event) {
      const mins = eventMinutes(
        event.clocked_in_at,
        event.clocked_out_at,
        event.break_minutes,
      )
      return (mins / 60).toFixed(1)
    },
    onApprove() {
      if (!this.event) return
      this.$emit('approve', this.event.id)
      this.close()
    },
    cancelReject() {
      this.rejecting = false
      this.rejectComment = ''
    },
    onReject() {
      if (!this.event) return
      this.$emit('reject', {
        eventId: this.event.id,
        adminComment: this.rejectComment || undefined,
      })
      this.close()
    },
    openLightbox(urls, index) {
      this.lightboxPhotos = urls.slice()
      this.lightboxIndex = index
      this.$bvModal.show(this.lightboxId)
    },
    lightboxPrev() {
      this.lightboxIndex =
        (this.lightboxIndex - 1 + this.lightboxPhotos.length) %
        this.lightboxPhotos.length
    },
    lightboxNext() {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.lightboxPhotos.length
    },
  },
}
</script>

<style lang="scss" scoped>
.entry-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.entry-worker {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.entry-project {
  margin-top: 2px;
  font-size: 13px;
  color: #6b7280;
}

.entry-times {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;
  margin-bottom: 12px;
}

.entry-times-icon {
  color: #9ca3af;
  flex-shrink: 0;
}

.entry-rejection {
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin-bottom: 12px;
}

.entry-rejection-label {
  font-size: 11px;
  font-weight: 700;
  color: #b91c1c;
}

.entry-rejection-text {
  margin-top: 2px;
  font-size: 13px;
  color: #991b1b;
}

.entry-section {
  margin-bottom: 14px;
}

.entry-section-label {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 6px;
}

.entry-comment-text {
  font-size: 13px;
  color: #374151;
}

.entry-empty {
  font-size: 13px;
  color: #9ca3af;
  font-style: italic;
}

.entry-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.entry-photo-wrap {
  cursor: pointer;
}

.entry-photo-thumb {
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.entry-plant-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.entry-plant-line {
  font-size: 13px;
  color: #374151;
}

.entry-plant-qty {
  color: #6b7280;
}

.entry-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;

  > .b-button,
  > button {
    width: 100%;
  }
}

.entry-reject-buttons {
  display: flex;
  gap: 8px;

  > .b-button,
  > button {
    flex: 1;
  }
}
</style>
